import { SerializedChartType } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import { CHART_TYPES } from '@/composables/charts/influx-charts/chart-builders/CHART_TYPES'
import { GeneralChartTimeSetup } from '@/composables/charts/influx-charts/chart-serialization/ChartTimeSetup'
import { ANNOTATOR_TYPES } from '@/composables/charts/influx-charts/ChartIncidentAnnotators'
import { useI18n } from 'vue-i18n'

export type ChartSuggestion = {
  id: string
  title: string
  description: string
  serializedChart: SerializedChartType
}

const chartSuggestions: ChartSuggestion[] = [
  {
    id: 'default-activity',
    title: 'chart_sharing.creator.suggestion.charts.default_activity_chart',
    description: 'chart_sharing.creator.suggestion.charts.default_activity_chart_description',
    serializedChart: {
      chartType: CHART_TYPES.LINE,
      measurement: 'activity',
      field: 'activity',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.activity',
      subtitle: 'charts.subtitles.activityGlobal',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
    },
  },
  {
    id: 'default-speed',
    title: 'chart_sharing.creator.suggestion.charts.default_speed_chart',
    description: 'chart_sharing.creator.suggestion.charts.default_speed_chart_description',
    serializedChart: {
      chartType: CHART_TYPES.LINE,
      measurement: 'speed',
      field: 'speed',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.speed',
      subtitle: 'charts.subtitles.speedGlobal',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
    },
  },
  {
    id: 'default-grid-score',
    title: 'chart_sharing.creator.suggestion.charts.default_grid_score_chart',
    description: 'chart_sharing.creator.suggestion.charts.default_grid_score_chart_description',
    serializedChart: {
      chartType: CHART_TYPES.LINE,
      measurement: 'buckets',
      field: 'gridScore',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.gridScore',
      subtitle: 'charts.subtitles.gridScore',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
    },
  },
  {
    id: 'camera-based-speed',
    title: 'chart_sharing.creator.suggestion.charts.camera_based_speed_chart',
    description: 'chart_sharing.creator.suggestion.charts.camera_based_speed_chart_description',
    serializedChart: {
      chartType: CHART_TYPES.CAMERA_BASED_LINE,
      measurement: 'speed',
      field: 'speed',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.speed',
      subtitle: 'charts.subtitles.speedPerCamera',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      cameraTagSelector: { tag: 'camera', allowEmptyValues: false, useAll: true, groupByValues: true },
    },
  },
  {
    id: 'animal-counts-front-back',
    title: 'chart_sharing.creator.suggestion.charts.animal_counts_front_back',
    description: 'chart_sharing.creator.suggestion.charts.animal_counts_front_back_description',
    serializedChart: {
      chartType: CHART_TYPES.REGION_SUMS,
      field: 'meanCount',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.regions',
      subtitle: 'charts.subtitles.regionsFrontBack',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      regionSumProperty: 'stallbereich_vh',
      regionSumValueTagSelector: { tag: 'value', useAll: true, allowEmptyValues: false, groupByValues: true },
    },
  },
  {
    id: 'animal-counts-left-right',
    title: 'chart_sharing.creator.suggestion.charts.animal_counts_left_right',
    description: 'chart_sharing.creator.suggestion.charts.animal_counts_left_right_description',
    serializedChart: {
      chartType: CHART_TYPES.REGION_SUMS,
      field: 'meanCount',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.regions',
      subtitle: 'charts.subtitles.regionsLeftRight',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      regionSumProperty: 'stallbereich_lr',
      regionSumValueTagSelector: { tag: 'value', useAll: true, allowEmptyValues: false, groupByValues: true },
    },
  },
  {
    id: 'animal-counts-barn-equipment',
    title: 'chart_sharing.creator.suggestion.charts.animal_counts_barn_equipment',
    description: 'chart_sharing.creator.suggestion.charts.animal_counts_barn_equipment_description',
    serializedChart: {
      chartType: CHART_TYPES.REGION_SUMS,
      field: 'meanCount',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.regions',
      subtitle: 'charts.subtitles.regionsBarnEquipment',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      regionSumProperty: 'stalltechnik',
      regionSumValueTagSelector: { tag: 'value', groupByValues: true, useAll: true, allowEmptyValues: false },
    },
  },
  {
    id: 'animal-counts-cameras',
    title: 'chart_sharing.creator.suggestion.charts.animal_counts_cameras',
    description: 'chart_sharing.creator.suggestion.charts.animal_counts_cameras_description',
    serializedChart: {
      chartType: CHART_TYPES.REGION_SUMS,
      field: 'meanCount',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.labelCounts',
      subtitle: 'charts.subtitles.labelCountsPerCamera',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      regionSumProperty: 'camera',
      regionSumValueTagSelector: { tag: 'value', useAll: true, allowEmptyValues: false, groupByValues: true },
    },
  },
  {
    id: 'animal-counts-global',
    title: 'chart_sharing.creator.suggestion.charts.animal_counts_global',
    description: 'chart_sharing.creator.suggestion.charts.animal_counts_global_description',
    serializedChart: {
      chartType: CHART_TYPES.REGION_SUMS,
      field: 'meanCount',
      xAggregation: 'mean',
      timeSetup: GeneralChartTimeSetup.lastNDays(4).serialize(),
      title: 'charts.titles.labelCounts',
      subtitle: 'charts.subtitles.labelCountsGlobal',
      incidentAnnotators: [{ annotatorType: ANNOTATOR_TYPES.BARNDOC }, { annotatorType: ANNOTATOR_TYPES.SPARSE_DICT_ANOMALY }],
      incidentReducer: { reducerType: 'penState' },
      regionSumProperty: 'label',
      regionSumValueTagSelector: { tag: 'value', useAll: true, allowEmptyValues: false, groupByValues: true },
    },
  },
]

export function useChartSuggestions(): ChartSuggestion[] {
  const { t } = useI18n()

  return chartSuggestions.map(suggestion => ({
    ...suggestion,
    title: t(suggestion.title),
    description: t(suggestion.description),
    serializedChart: {
      ...suggestion.serializedChart,
      title: t(suggestion.serializedChart.title!),
      subtitle: t(suggestion.serializedChart.subtitle!),
    },
  }))
}
